import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'
import { formatNumber } from '../../utils/formatNumber'
import { useTranslation } from 'react-i18next'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

export interface TrendChartProps {
  trends: YearTrend[]
}

export interface YearTrend {
  year: string
  scope1: number
  scope2: number
  scope3: number
}

export const TrendChart = ({ trends }: TrendChartProps) => {
  const { t } = useTranslation()

  // Sort trends by year and add current year if not present
  let sortedTrends = [...trends]
    .map((trend) => ({
      ...trend,
      scope1: trend.scope1 / 1000,
      scope2: trend.scope2 / 1000,
      scope3: trend.scope3 / 1000,
    }))
    .sort((a, b) => a.year.localeCompare(b.year))

  // Add next year (grayed out)
  const nextYear = (
    Number(sortedTrends[sortedTrends.length - 1].year) + 1
  ).toString()
  sortedTrends = sortedTrends.concat({
    year: nextYear,
    scope1: trends[trends.length - 1]?.scope1 / 1000,
    scope2: trends[trends.length - 1]?.scope2 / 1000,
    scope3: trends[trends.length - 1]?.scope3 / 1000,
  })

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const value = context.raw
            const year = context.label
            if (year === nextYear) {
              return `${context.dataset.label}: ${t(
                'dashboard.charts.placeholder'
              )}`
            }
            return `${context.dataset.label}: ${formatNumber(value)} t`
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        ticks: {
          callback: function (this: any, tickValue: number | string) {
            if (typeof tickValue === 'number') {
              return tickValue + ' t'
            }
            return tickValue + ' t'
          },
        },
      },
    },
    barThickness: 20,
    maxBarThickness: 30,
  } as const

  const data = {
    labels: sortedTrends.map((trend) => trend.year),
    datasets: [
      {
        label: 'Scope 1',
        data: sortedTrends.map((trend) => trend.scope1),
        backgroundColor: sortedTrends.map((trend) =>
          trend.year === nextYear
            ? 'rgba(59, 130, 246, 0.1)'
            : 'rgba(59, 130, 246, 0.7)'
        ),
        borderColor: sortedTrends.map((trend) =>
          trend.year === nextYear ? 'rgba(59, 130, 246, 0.2)' : 'transparent'
        ),
        borderWidth: sortedTrends.map((trend) =>
          trend.year === nextYear ? 2 : 0
        ),
        borderDash: sortedTrends.map((trend) =>
          trend.year === nextYear ? [5, 5] : []
        ),
        borderRadius: 4,
      },
      {
        label: 'Scope 2',
        data: sortedTrends.map((trend) => trend.scope2),
        backgroundColor: sortedTrends.map((trend) =>
          trend.year === nextYear
            ? 'rgba(16, 185, 129, 0.1)'
            : 'rgba(16, 185, 129, 0.7)'
        ),
        borderColor: sortedTrends.map((trend) =>
          trend.year === nextYear ? 'rgba(16, 185, 129, 0.2)' : 'transparent'
        ),
        borderWidth: sortedTrends.map((trend) =>
          trend.year === nextYear ? 2 : 0
        ),
        borderDash: sortedTrends.map((trend) =>
          trend.year === nextYear ? [5, 5] : []
        ),
        borderRadius: 4,
      },
      {
        label: 'Scope 3',
        data: sortedTrends.map((trend) => trend.scope3),
        backgroundColor: sortedTrends.map((trend) =>
          trend.year === nextYear
            ? 'rgba(245, 158, 11, 0.1)'
            : 'rgba(245, 158, 11, 0.7)'
        ),
        borderColor: sortedTrends.map((trend) =>
          trend.year === nextYear ? 'rgba(245, 158, 11, 0.2)' : 'transparent'
        ),
        borderWidth: sortedTrends.map((trend) =>
          trend.year === nextYear ? 2 : 0
        ),
        borderDash: sortedTrends.map((trend) =>
          trend.year === nextYear ? [5, 5] : []
        ),
        borderRadius: 4,
      },
    ],
  }

  return <Bar options={options} data={data} />
}
